import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Group, Image, SimpleGrid, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { currentPosition, preparePhoto } from '@prooffleet/client';
import { putBlob } from '../offline/db.js';
import { previewsFor, type Preview } from '../media/previews.js';

interface PhotoCaptureProps {
  draftId: string;
  photoIds: string[];
  required: boolean;
  onChange: (photoIds: string[], gps: { lat: number; lon: number; accuracyMeters: number } | null) => void;
}

/**
 * Съёмка узла.
 *
 * `capture="environment"` открывает камеру, а не галерею: на iOS и Android
 * это единственный способ не дать подложить старый снимок штатным путём.
 * Полностью подмену это не исключает (можно сфотографировать чужой экран),
 * поэтому основная защита — серверные сигналы, а не интерфейс.
 */
export function PhotoCapture({
  draftId,
  photoIds,
  required,
  onChange,
}: PhotoCaptureProps): React.JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previews, setPreviews] = useState<Preview[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ключ вместо массива: `photoIds` приходит новым массивом на каждый рендер,
  // и зависимость от него зациклила бы эффект.
  const photoKey = photoIds.join(',');

  /**
   * Превью выводятся из photoIds, а не копятся в локальном состоянии.
   *
   * Компонент не пересоздаётся при переходе к следующему пункту, поэтому
   * накопленный список показывал снимок предыдущего узла рядом с ошибкой
   * «фото обязательно» — водитель видел фотографию и не понимал претензии.
   * Обратный случай не менее важен: возврат «Назад» к снятому пункту и
   * восстановление черновика после разряженного телефона обязаны показать
   * то, что уже снято.
   */
  useEffect(() => {
    const ids = photoKey === '' ? [] : photoKey.split(',');
    const urls: string[] = [];
    let cancelled = false;

    void (async () => {
      const loaded = await previewsFor(ids);
      urls.push(...loaded.map((p) => p.url));
      if (cancelled) {
        urls.forEach((u) => { URL.revokeObjectURL(u); });
        return;
      }
      setPreviews(loaded);
    })();

    return () => {
      cancelled = true;
      urls.forEach((u) => { URL.revokeObjectURL(u); });
    };
  }, [photoKey]);

  const handleFile = async (file: File): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const prepared = await preparePhoto(file);
      const localId = crypto.randomUUID();

      await putBlob({
        localId,
        draftId,
        bytes: prepared.bytes.slice().buffer,
        sha256: prepared.sha256,
        mime: 'image/jpeg',
        extension: 'jpg',
        objectKey: null,
        versionId: null,
      });

      // Координаты снимаются в момент съёмки: человек, обходящий тягач,
      // смещается на десятки метров, и это смещение — доказательство обхода.
      onChange([...photoIds, localId], await currentPosition(4000));
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
      if (inputRef.current !== null) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          if (file !== undefined) void handleFile(file);
        }}
      />

      {previews.length > 0 && (
        <SimpleGrid cols={previews.length === 1 ? 1 : 2} mb="sm">
          {previews.map((preview) => (
            <Image key={preview.localId} src={preview.url} radius="md" alt="" />
          ))}
        </SimpleGrid>
      )}

      {error !== null && (
        <Alert color="red" mb="sm">
          {error}
        </Alert>
      )}

      <Group grow>
        <Button
          variant={photoIds.length === 0 && required ? 'filled' : 'light'}
          loading={busy}
          onClick={() => inputRef.current?.click()}
        >
          {photoIds.length === 0 ? t('inspection.photo') : t('inspection.retake')}
        </Button>
      </Group>

      {required && photoIds.length === 0 && (
        <Text c="red" fz="sm" mt={6}>
          {t('inspection.photoRequired')}
        </Text>
      )}
    </div>
  );
}
